import type { Role } from '../../pages/users/types/rbac';

const ADMIN_NOTE_ROLES: readonly Role[] = [
  'unify-admin',
  'unify-assistant',
  'practice-owner',
  'practice-admin',
];

/** True when the user may create or edit admin notes on a client chart. */
export function canCreateAdminNote(role: Role): boolean {
  return ADMIN_NOTE_ROLES.includes(role);
}

const CLINICAL_NOTE_ROLES: readonly Role[] = ['provider', 'supervisor'];

/**
 * True when the user may create or modify clinical chart notes (chart notes, progress notes,
 * revisions). Admin/practice staff manage admin notes only.
 */
export function canAuthorClinicalNote(role: Role): boolean {
  return CLINICAL_NOTE_ROLES.includes(role);
}

/**
 * Role-set variant: a user who also holds a provider/supervisor role keeps clinical
 * authorship while acting under an admin role, since the care-manager check still applies.
 */
export function canAuthorClinicalNoteForRoles(roles: readonly Role[]): boolean {
  return roles.some(canAuthorClinicalNote);
}

/** True when the user may read psychotherapy notes. Care-team clinical roles only. */
export function canViewPsychotherapyNote(role: Role): boolean {
  return canAuthorClinicalNote(role);
}

/**
 * True when the viewer is the episode care manager (session provider). Falls back to the
 * appointment practitioner when the episode has no careManager.
 */
export function isSessionCareManager(args: {
  viewerPractitionerId?: string;
  episodeCareManagerPractitionerId?: string;
  appointmentPractitionerId?: string;
}): boolean {
  const viewer = args.viewerPractitionerId?.trim();
  if (!viewer) return false;
  const careManager =
    args.episodeCareManagerPractitionerId?.trim() ||
    args.appointmentPractitionerId?.trim();
  return Boolean(careManager && careManager === viewer);
}
